import { IBFActivate, IBF_ACTIVATE } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_ACTIVATE";
import { IBFBasalRate } from "@/lib/drivers/insulet/IBFHistoryLogs/IBF_BASAL_RATE";
import { IBFLogRecord, IBFLogRecordHistory } from "@/lib/drivers/insulet/IBFRecords/IBF_LOG_RECORD";
import { OmnipodData, omnipodVersionToString } from "@/lib/drivers/insulet/OmnipodTypes";
import { OmnipodTransformer } from "@/lib/drivers/insulet/transformers/OmnipodTransformer";
import {
  IBGInterruptionEvent,
  getBasalInsulinDeliveryRanges,
  isBasalInterruptionEvent,
  isBasalRateEvent,
  splitRangesByDay,
} from "@/lib/drivers/insulet/transformers/OmnipodTransformerUtils";
import { PumpEventContentDTO, PumpEventDTO } from "@/models/PumpEventModel";

type OmnipodPumpEvents = IBFActivate;

type OmnipodPumpEventLog = IBFLogRecordHistory<OmnipodPumpEvents>;

export class OmnipodPumpEventsTransformer implements OmnipodTransformer<PumpEventDTO[]> {
  _isPodChangeEvent = (event: IBFLogRecord): event is IBFLogRecordHistory<IBFActivate> =>
    event.type === "HISTORY" && event.history.type === IBF_ACTIVATE.value;

  _mapEventCategory = (event: OmnipodPumpEventLog): PumpEventContentDTO => {
    if (this._isPodChangeEvent(event)) {
      return {
        category: "pod_changed",
        data: {
          pod_info: {
            lot_number: event.history.data.lotNumber,
            seq_number: event.history.data.seqNumber,
            processor_version: omnipodVersionToString(event.history.data.processor),
          },
        },
      };
    }
    // Can't happen
    throw new Error("Invalid event");
  };

  getPumpEvents = (pumpEvents: OmnipodPumpEventLog[]): PumpEventDTO[] => {
    const result: PumpEventDTO[] = [];
    for (const event of pumpEvents) {
      result.push({
        sequence_id: event.logIndex,
        date: event.date.toISO() ?? "",
        ...this._mapEventCategory(event),
      });
    }

    return result;
  };

  getDailyBasalInsulin = (
    basalRateEvents: IBFLogRecordHistory<IBFBasalRate>[],
    basalDeliveryInterruptEvent: IBGInterruptionEvent[],
  ): PumpEventDTO[] => {
    const result: PumpEventDTO[] = [];
    const ranges = getBasalInsulinDeliveryRanges(basalRateEvents, basalDeliveryInterruptEvent);

    const splitRanges = splitRangesByDay(ranges);

    for (const day of splitRanges) {
      const totalInsulin = day.ranges.reduce((acc, range) => {
        const duration = range.end.diff(range.start, "minutes").minutes;
        const insulinInRange = (range.basalRate * duration) / 60; // U/h * h => U
        return acc + insulinInRange;
      }, 0);
      result.push({
        sequence_id: day.ranges[0].logIndex,
        date: day.day.toISO() ?? "",
        category: "daily_basal",
        data: {
          daily_total_basal: totalInsulin,
        },
      });
    }

    return result;
  };

  transform = (data: OmnipodData): PumpEventDTO[] => {
    const pumpEvents = data.logs.filter(e => this._isPodChangeEvent(e));
    const basalRateEvents = data.logs.filter(isBasalRateEvent);
    const basalRateInterruptionEvent = data.logs.filter(isBasalInterruptionEvent);
    const result = this.getPumpEvents(pumpEvents);
    result.push(...this.getDailyBasalInsulin(basalRateEvents, basalRateInterruptionEvent));
    return result;
  };
}
